// Tab context menu (right-click on desktop, long-press on touch): pin, duplicate, find in page,
// close / close others / close to the right, reopen closed. Find in page is here because an iPad
// or phone with no keyboard has no Ctrl+F.
import { closeTab, closeOthers, closeRight, pinTab, selectTab, moveTab } from "./tabs.js";
import { duplicateActive, reopenClosed } from "./session.js";
import { state, byId } from "./state.js";
import { reloadWithEngine } from "./errors.js";
import { engineLabel } from "../shared/settings-schema.js";
import { openFind } from "./zoom.js";

let menuEl = null;
function ensureMenu() {
	if (menuEl) return menuEl;
	menuEl = document.createElement("div");
	menuEl.className = "rv-ctxmenu";
	menuEl.hidden = true;
	menuEl.setAttribute("role", "menu");
	menuEl.setAttribute("aria-label", "Tab");
	document.body.appendChild(menuEl);
	document.addEventListener("pointerdown", (e) => { if (menuEl && !menuEl.hidden && !menuEl.contains(e.target)) hideMenu(); }, true);
	document.addEventListener("keydown", (e) => { if (e.key === "Escape" && menuEl && !menuEl.hidden) { e.preventDefault(); hideMenu(true); } });
	menuEl.addEventListener("keydown", onMenuKey);
	window.addEventListener("blur", () => hideMenu());
	window.addEventListener("resize", () => hideMenu());
	return menuEl;
}
let returnTo = null; // the tab id focus goes back to when the menu closes from the keyboard
function hideMenu(restore) {
	if (!menuEl || menuEl.hidden) return;
	const inside = menuEl.contains(document.activeElement);
	menuEl.hidden = true;
	if ((restore || inside) && returnTo != null) {
		const tab = document.querySelector('.rv-tab[tabindex="0"]');
		if (tab) tab.focus();
	}
	returnTo = null;
}
// Menu keyboard: arrows, Home/End move between items; Tab closes the menu.
function onMenuKey(e) {
	const items = [...menuEl.querySelectorAll(".rv-ctxitem:not([disabled])")];
	const i = items.indexOf(document.activeElement);
	let to = -1;
	if (e.key === "ArrowDown") to = (i + 1) % items.length;
	else if (e.key === "ArrowUp") to = (i - 1 + items.length) % items.length;
	else if (e.key === "Home") to = 0;
	else if (e.key === "End") to = items.length - 1;
	else if (e.key === "Tab") { e.preventDefault(); hideMenu(true); return; }
	if (to < 0) return;
	e.preventDefault();
	items[to].focus();
}

let kbOpenedAt = 0;
export function openTabMenu(id, x, y, fromKeyboard) {
	const t = byId(id);
	if (!t) return;
	// Shift+F10 can also produce a contextmenu event right after our keydown handler opened the menu.
	if (!fromKeyboard && menuEl && !menuEl.hidden && Date.now() - kbOpenedAt < 400) return;
	if (fromKeyboard) kbOpenedAt = Date.now();
	const m = ensureMenu();
	m.textContent = "";
	returnTo = id;
	const add = (label, fn, disabled) => {
		const b = document.createElement("button");
		b.className = "rv-ctxitem"; b.type = "button"; b.textContent = label; b.setAttribute("role", "menuitem"); b.tabIndex = -1;
		if (disabled) b.disabled = true;
		else b.addEventListener("click", () => { const kb = m.contains(document.activeElement); returnTo = null; hideMenu(); fn(); if (kb) { const tab = document.querySelector('.rv-tab[tabindex="0"]'); if (tab) tab.focus(); } });
		m.appendChild(b);
	};
	const sep = () => { const d = document.createElement("div"); d.className = "rv-ctxsep"; d.setAttribute("role", "separator"); m.appendChild(d); };
	add(t.pinned ? "Unpin tab" : "Pin tab", () => pinTab(id));
	add("Duplicate", () => { selectTab(id); duplicateActive(); });
	// A page that looks wrong on Standard can be retried on Standard (v1) (and back), for this site in this tab.
	// Not offered when this page is on Standard (v1) because Standard cannot run here (paused, this
	// browser, an older worker, a failed start): it would just reload on Standard (v1) again.
	if (t.url && t.src && !t.internal && (t.engine === "scramjet2" || (t.engine === "scramjet" && (!t.engineWhy || t.engineWhy === "site" || t.engineWhy === "manual")))) {
		const other = t.engine === "scramjet2" ? "scramjet" : "scramjet2";
		add("Reload with " + engineLabel(other), () => reloadWithEngine(id, other));
	}
	if (t.src) add("Find in page", () => { selectTab(id); setTimeout(openFind, 0); }); // after the menu hands focus back to the tab
	const at = state.tabs.findIndex((x) => x.id === id);
	add("Move left", () => moveTab(id, -1), at <= 0);
	add("Move right", () => moveTab(id, 1), at >= state.tabs.length - 1);
	sep();
	add("Close", () => closeTab(id));
	add("Close others", () => closeOthers(id), state.tabs.filter((x) => !x.pinned).length <= 1);
	add("Close to the right", () => closeRight(id), state.tabs.findIndex((x) => x.id === id) >= state.tabs.length - 1);
	sep();
	add("Reopen closed tab", () => reopenClosed(), !state.closed.length);
	m.hidden = false;
	const w = m.offsetWidth || 180, h = m.offsetHeight || 220;
	m.style.left = Math.max(4, Math.min(x, window.innerWidth - w - 6)) + "px";
	m.style.top = Math.max(4, Math.min(y, window.innerHeight - h - 6)) + "px";
	if (fromKeyboard) { const first = m.querySelector(".rv-ctxitem:not([disabled])"); if (first) first.focus(); }
}

// Long-press (touch) → context menu.
export function attachLongPress(el, cb) {
	let timer = null, sx = 0, sy = 0;
	const cancel = () => { clearTimeout(timer); timer = null; };
	el.addEventListener("touchstart", (e) => {
		const tc = e.touches[0]; sx = tc.clientX; sy = tc.clientY;
		timer = setTimeout(() => cb(sx, sy), 500);
	}, { passive: true });
	el.addEventListener("touchend", cancel);
	el.addEventListener("touchcancel", cancel);
	el.addEventListener("touchmove", (e) => { const tc = e.touches[0]; if (Math.abs(tc.clientX - sx) > 10 || Math.abs(tc.clientY - sy) > 10) cancel(); });
}
